/* Savaaya Atelier — global.js (vanilla, no dependencies) */
(() => {
  const theme = window.theme || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* Money ------------------------------------------------------------------ */
  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const num = (n / 100).toFixed(precision);
      const [whole, dec] = num.split('.');
      return whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (dec ? decimal + dec : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'"); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' '); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(match[0], value);
  };
  theme.formatMoney = formatMoney;

  /* Toast ------------------------------------------------------------------ */
  let toastTimer;
  const toast = (msg) => {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };
  theme.toast = toast;

  /* Scroll lock ------------------------------------------------------------ */
  let openCount = 0;
  const lockScroll = () => { openCount++; document.body.classList.add('is-locked'); };
  const unlockScroll = () => { openCount = Math.max(0, openCount - 1); if (!openCount) document.body.classList.remove('is-locked'); };

  /* Drawer / modal base --------------------------------------------------- */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) this.close(); });
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
    }
    get panel() { return this.querySelector('.drawer__panel, .modal__panel'); }
    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this._trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      requestAnimationFrame(() => {
        const focusable = this.querySelector('[autofocus], [data-input]') || this.panel;
        focusable && focusable.focus({ preventScroll: true });
      });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this._trigger && this._trigger.focus) this._trigger.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const target = document.getElementById(opener.dataset.drawerOpen);
      if (target && target.open) { e.preventDefault(); target.open(opener); }
    }
  });

  /* Sticky header --------------------------------------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      const section = this.closest('.shopify-section');
      const sticky = this.dataset.sticky === 'true';
      if (section && !sticky) section.style.position = 'relative';
      const setHeight = () => document.documentElement.style.setProperty('--header-h', `${sticky && section ? section.offsetHeight : 0}px`);
      setHeight();
      window.addEventListener('resize', debounce(setHeight, 150));
      if (!sticky) return;
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          document.body.classList.toggle('is-scrolled', window.scrollY > 40);
          ticking = false;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      this.querySelectorAll('.nav__item').forEach((item) => {
        item.addEventListener('mouseleave', () => item.classList.remove('is-open'));
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* Announcement bar ------------------------------------------------------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      const items = $$('.announcement__item', this);
      if (items.length < 2) return;
      let i = 0;
      const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      this._timer = setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, speed);
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* Quantity input -------------------------------------------------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[name]');
        if (!btn) return;
        const step = btn.name === 'plus' ? 1 : -1;
        const min = parseInt(this.input.min || '0', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
        if (next === parseInt(this.input.value, 10)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Cart ------------------------------------------------------------------- */
  const cartSections = () => {
    const ids = ['cart-drawer'];
    const main = $('[data-main-cart]');
    if (main) ids.push(main.dataset.sectionId);
    return ids;
  };

  const updateCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.dataset.count = count;
      el.textContent = count > 0 ? count : '';
    });
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    const parser = new DOMParser();
    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const doc = parser.parseFromString(drawerHTML, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer-inner]');
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.replaceWith(fresh);
      if (fresh) updateCount(parseInt(fresh.dataset.itemCount, 10) || 0);
    }
    const main = $('[data-main-cart]');
    if (main && sections[main.dataset.sectionId]) {
      const doc = parser.parseFromString(sections[main.dataset.sectionId], 'text/html');
      const fresh = doc.querySelector('[data-main-cart]');
      if (fresh) {
        main.replaceWith(fresh);
        updateCount(parseInt(fresh.dataset.itemCount, 10) || 0);
      }
    }
  };

  const postJSON = (url, body) => fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body)
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok || data.status) throw data;
    return data;
  });

  const afterAdd = (data) => {
    renderCartSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    const qv = $('#QuickView');
    if (qv && qv.classList.contains('is-open')) qv.close();
    const drawer = $('#CartDrawer');
    if (theme.cartType === 'drawer' && drawer) drawer.open();
    else if (theme.cartType === 'page') window.location.href = theme.routes.cart;
    else toast(theme.strings.added);
  };

  const addToCart = (items) => postJSON(`${theme.routes.cartAdd}.js`, {
    items,
    sections: cartSections().join(','),
    sections_url: window.location.pathname
  }).then(afterAdd);
  theme.addToCart = addToCart;

  const changeLine = (line, quantity) => postJSON(`${theme.routes.cartChange}.js`, {
    line, quantity, sections: cartSections().join(','), sections_url: window.location.pathname
  }).then((data) => {
    renderCartSections(data.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    return data;
  });

  class CartDrawer extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      if (this._cartBound) return;
      this._cartBound = true;
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-line-remove]');
        if (rm) this.update(parseInt(rm.dataset.lineRemove, 10), 0);
      });
    }
    onChange(e) {
      if (e.target.matches('[data-line-qty]')) this.update(parseInt(e.target.dataset.lineQty, 10), parseInt(e.target.value, 10) || 0);
      if (e.target.matches('[data-cart-note]')) postJSON(`${theme.routes.cartUpdate}.js`, { note: e.target.value }).catch(() => {});
    }
    update(line, qty) {
      this.classList.add('is-busy');
      changeLine(line, qty)
        .catch((err) => toast(err.description || err.message || theme.strings.cartError))
        .finally(() => this.classList.remove('is-busy'));
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Cart page lines use the same handlers */
  document.addEventListener('change', (e) => {
    const main = e.target.closest('[data-main-cart]');
    if (!main) return;
    if (e.target.matches('[data-line-qty]')) {
      main.classList.add('is-busy');
      changeLine(parseInt(e.target.dataset.lineQty, 10), parseInt(e.target.value, 10) || 0)
        .catch((err) => toast(err.description || theme.strings.cartError))
        .finally(() => main.classList.remove('is-busy'));
    }
    if (e.target.matches('[data-cart-note]')) postJSON(`${theme.routes.cartUpdate}.js`, { note: e.target.value }).catch(() => {});
  });
  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-main-cart] [data-line-remove]');
    if (rm) {
      e.preventDefault();
      changeLine(parseInt(rm.dataset.lineRemove, 10), 0).catch((err) => toast(err.description || theme.strings.cartError));
    }
    const toggle = e.target.closest('[data-cart-toggle]');
    const drawer = $('#CartDrawer');
    if (toggle && drawer && theme.cartType === 'drawer') {
      e.preventDefault();
      drawer.open(toggle);
    }
  });

  /* Product form ----------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || this._bound) return;
      this._bound = true;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    onSubmit(e) {
      e.preventDefault();
      const btn = this.querySelector('[type="submit"][name="add"]');
      if (!btn || btn.hasAttribute('disabled')) return;
      const errorEl = this.querySelector('[data-form-error]');
      if (errorEl) errorEl.hidden = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      const fd = new FormData(this.form);
      fd.append('sections', cartSections().join(','));
      fd.append('sections_url', window.location.pathname);
      fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      })
        .then(async (r) => {
          const data = await r.json();
          if (!r.ok || data.status) throw data;
          return data;
        })
        .then(afterAdd)
        .catch((err) => {
          const msg = err.description || err.message || theme.strings.cartError;
          if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; } else toast(msg);
        })
        .finally(() => { btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy'); });
    }
  }
  customElements.define('product-form', ProductForm);

  /* Variant picker ---------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      const json = this.querySelector('script[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.root = this.closest('[data-product-root]') || document;
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    selectedOptions() {
      return $$('fieldset[data-option-index]', this).map((fs) => {
        const checked = fs.querySelector('input:checked');
        const select = fs.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }
    onChange() {
      const opts = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      $$('fieldset[data-option-index]', this).forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = opts[i] || '';
      });
      this.markAvailability();
      this.update(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
    markAvailability() {
      const opts = this.selectedOptions();
      $$('fieldset[data-option-index]', this).forEach((fs) => {
        const idx = parseInt(fs.dataset.optionIndex, 10);
        $$('input[type="radio"]', fs).forEach((input) => {
          const available = this.variants.some((v) => v.available && v.options[idx] === input.value &&
            v.options.every((o, j) => j === idx || j > idx || o === opts[j]));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }
    update(variant) {
      const root = this.root;
      const btn = $('[name="add"]', root);
      const btnText = btn && btn.querySelector('[data-btn-text]');
      const idInput = $('input[name="id"]', root);
      const priceEl = $('[data-product-price]', root);
      const stockEl = $('[data-stock-note]', root);
      const skuEl = $('[data-sku]', root);
      const dynamic = $('.buy__dynamic', root);

      if (!variant) {
        if (btn) { btn.setAttribute('disabled', ''); if (btnText) btnText.textContent = theme.strings.unavailable; }
        if (dynamic) dynamic.style.display = 'none';
        return;
      }
      if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (btn) {
        btn.toggleAttribute('disabled', !variant.available);
        if (btnText) btnText.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
      }
      if (dynamic) dynamic.style.display = variant.available ? '' : 'none';
      if (priceEl) {
        const sale = variant.compare_at_price > variant.price;
        let html = `<span class="${sale ? 'price__sale' : ''}">${formatMoney(variant.price)}</span>`;
        if (sale) {
          const pct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
          html += `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`;
          if (priceEl.dataset.showSave === 'true') html += `<span class="price__save">${priceEl.dataset.saveLabel.replace('[percent]', pct)}</span>`;
        }
        priceEl.innerHTML = html;
      }
      if (skuEl) { skuEl.textContent = variant.sku || ''; skuEl.hidden = !variant.sku; }
      if (stockEl) {
        const qty = variant.inventory_quantity;
        const threshold = parseInt(stockEl.dataset.threshold, 10) || 0;
        const show = variant.available && variant.inventory_management === 'shopify' && qty > 0 && qty <= threshold;
        stockEl.hidden = !show;
        if (show) stockEl.textContent = theme.strings.lowStock.replace('[count]', qty);
      }
      const qtyInput = $('[data-product-qty]', root);
      if (qtyInput) {
        if (qtyInput.dataset.limit === 'true' && variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') qtyInput.max = variant.inventory_quantity;
        else qtyInput.removeAttribute('max');
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Slider ------------------------------------------------------------------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bar = this.querySelector('[data-progress]');
      const step = (dir) => {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const amount = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
        const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / amount));
        this.track.scrollBy({ left: dir * amount * visible, behavior: 'smooth' });
      };
      this.prev && this.prev.addEventListener('click', () => step(-1));
      this.next && this.next.addEventListener('click', () => step(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      if (this.prev) this.prev.disabled = scrollLeft <= 2;
      if (this.next) this.next.disabled = scrollLeft >= max - 2;
      if (this.bar) {
        const ratio = Math.min(1, clientWidth / scrollWidth);
        const pos = max > 0 ? scrollLeft / max : 0;
        this.bar.style.setProperty('--progress-w', `${ratio * 100}%`);
        this.bar.style.setProperty('--progress-x', `${pos * ((1 - ratio) / ratio) * 100}%`);
      }
      this.classList.toggle('is-static', max <= 2);
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* Quick view -------------------------------------------------------------- */
  class QuickViewModal extends DrawerPanel {
    load(url) {
      const content = this.querySelector('[data-content]');
      content.innerHTML = '';
      content.classList.add('is-loading');
      this.open();
      const clean = url.split('?')[0];
      fetch(`${clean}?section_id=quick-view`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('.qv');
          content.classList.remove('is-loading');
          if (!inner) throw new Error('Missing');
          content.innerHTML = '';
          content.appendChild(inner);
          if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
          initQvGallery(inner);
          document.dispatchEvent(new CustomEvent('quickview:loaded', { detail: { element: inner } }));
        })
        .catch(() => { window.location.href = url; });
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);

  const initQvGallery = (root) => {
    const track = root.querySelector('.qv__track');
    const dots = $$('.qv__dots span', root);
    if (!track || !dots.length) return;
    track.addEventListener('scroll', debounce(() => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
    }, 60), { passive: true });
  };

  document.addEventListener('click', (e) => {
    const qv = e.target.closest('[data-quick-view]');
    if (qv) {
      e.preventDefault();
      const modal = $('#QuickView');
      if (modal) modal.load(qv.dataset.quickView);
      else window.location.href = qv.dataset.quickView;
      return;
    }
    const qa = e.target.closest('[data-quick-add]');
    if (qa) {
      e.preventDefault();
      qa.classList.add('is-loading');
      addToCart([{ id: parseInt(qa.dataset.quickAdd, 10), quantity: 1 }])
        .catch((err) => toast(err.description || theme.strings.cartError))
        .finally(() => qa.classList.remove('is-loading'));
    }
  });

  /* Predictive search ------------------------------------------------------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-input]');
      this.results = this.querySelector('[data-results]');
      this.popular = this.querySelector('[data-popular]');
      if (this.dataset.enabled !== 'true' || !this.input) return;
      this.input.addEventListener('input', debounce(() => this.search(), 280));
    }
    search() {
      const q = this.input.value.trim();
      if (this.popular) this.popular.hidden = q.length > 0;
      if (!q) { this.results.innerHTML = ''; return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,query&resources[limit]=5&resources[options][unavailable_products]=last&section_id=predictive-search`;
      fetch(url, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const sec = doc.querySelector('#shopify-section-predictive-search');
          this.results.innerHTML = sec ? sec.innerHTML : '';
        })
        .catch(() => {});
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* Newsletter popup -------------------------------------------------------- */
  class NewsletterPopup extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      const key = 'savaaya-popup';
      const design = this.dataset.design === 'true';
      if (this.dataset.mobile === 'false' && window.innerWidth < 750) return;
      let last = 0;
      try { last = parseInt(localStorage.getItem(key) || '0', 10); } catch (e) { /* storage blocked */ }
      const days = parseInt(this.dataset.days, 10) || 2;
      const posted = this.querySelector('.form-message');
      if (posted) { this.open(); return; }
      if (!design && last && Date.now() - last < days * 864e5) return;
      setTimeout(() => this.open(), design ? 0 : (parseInt(this.dataset.delay, 10) || 6) * 1000);
      this.addEventListener('drawer:close', () => { try { localStorage.setItem(key, Date.now()); } catch (e) { /* ignore */ } });
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);

  /* Reveal on scroll -------------------------------------------------------- */
  const initReveal = (ctx = document) => {
    if (!document.body.classList.contains('has-reveal')) return;
    const els = $$('.reveal:not(.is-in)', ctx);
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
  };
  theme.initReveal = initReveal;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initReveal());
  else initReveal();

  /* Theme editor ----------------------------------------------------------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
    document.addEventListener('shopify:section:select', (e) => $$('.reveal', e.target).forEach((el) => el.classList.add('is-in')));
  }

  /* Pre-hover warmup of product pages (instant feel) ------------------------ */
  const warmed = new Set();
  document.addEventListener('mouseover', (e) => {
    const a = e.target.closest('a[href*="/products/"]');
    if (!a || warmed.has(a.href) || warmed.size > 20) return;
    warmed.add(a.href);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = a.href;
    document.head.appendChild(link);
  }, { passive: true });
})();
